import { Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconCirclePlus, IconRefresh, IconTrash, IconWand } from '@tabler/icons-react';
import { useState, useMemo } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

const STATUS_COLOR: Record<string, string> = {
  APPROVED: 'green',
  PENDING: 'yellow',
  REJECTED: 'red',
  PAUSED: 'orange',
  DISABLED: 'gray',
  IN_APPEAL: 'blue',
  PENDING_DELETION: 'gray',
};
const QUALITY_COLOR: Record<string, string> = {
  GREEN: 'green',
  YELLOW: 'yellow',
  RED: 'red',
  UNKNOWN: 'gray',
  PENDING: 'gray',
};
const STATUS_FILTERS = ['all', 'APPROVED', 'PENDING', 'REJECTED', 'PAUSED', 'DISABLED'];
const CATEGORY_FILTERS = ['all', 'MARKETING', 'UTILITY', 'AUTHENTICATION'];

/** Structural variant badge — carousel (with card count) or limited-time offer. */
function variantBadge(components: unknown): { label: string } | null {
  if (!Array.isArray(components)) return null;
  for (const c of components) {
    const t = String((c as { type?: string }).type ?? '').toUpperCase();
    if (t === 'CAROUSEL') {
      const cards = (c as { cards?: unknown[] }).cards;
      return { label: `Carousel · ${Array.isArray(cards) ? cards.length : '?'} cards` };
    }
    if (t === 'LIMITED_TIME_OFFER') return { label: 'LTO' };
  }
  return null;
}

/** WhatsApp message templates — the "WhatsApp" tab of the Templates hub.
 *  Create/edit live on the dedicated `/vendor/templates/new` + `/$name` routes. */
export function WhatsAppTemplatesPanel() {
  const list = trpc.whatsapp.templates.list.useQuery();
  const utils = trpc.useUtils();
  const sync = trpc.whatsapp.templates.syncFromMeta.useMutation({
    onSuccess: (r) => {
      void utils.whatsapp.templates.list.invalidate();
      notifications.show({
        message: `Synced ${r.synced} template(s) from Meta`,
        color: 'green',
      });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const del = trpc.whatsapp.templates.delete.useMutation({
    onSuccess: () => {
      void utils.whatsapp.templates.list.invalidate();
      notifications.show({ message: 'Template deleted', color: 'green' });
      setConfirmDelete(null);
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [confirmDelete, setConfirmDelete] = useState<{
    name: string;
    language: string;
  } | null>(null);

  const rows = useMemo(() => {
    const all = list.data ?? [];
    const f = filter.toLowerCase();
    return all.filter((t) => {
      if (f && !t.name.toLowerCase().includes(f) && !t.language.toLowerCase().includes(f))
        return false;
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      return true;
    });
  }, [list.data, filter, statusFilter, categoryFilter]);

  return (
    <>
      <Stack gap="md">
        <Group justify="flex-end">
          <Button
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={sync.isPending}
            onClick={() => sync.mutate()}
          >
            Sync from Meta
          </Button>
          <Button
            component={Link}
            to="/vendor/templates/new"
            leftSection={<IconCirclePlus size={14} />}
          >
            New template
          </Button>
        </Group>
        <Group>
          <TextInput
            placeholder="Filter by name / language…"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            data={STATUS_FILTERS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v ?? 'all')}
            placeholder="Status"
          />
          <Select
            data={CATEGORY_FILTERS}
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v ?? 'all')}
            placeholder="Category"
          />
        </Group>
        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={300} m="md" />
          ) : list.isError ? (
            <Text c="red" p="md">
              {list.error.message}
            </Text>
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Language</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Quality</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((t) => (
                  <Table.Tr key={t.id}>
                    <Table.Td>
                      <Anchor
                        renderRoot={(props) => (
                          <Link to="/vendor/templates/$name" params={{ name: t.name }} {...props} />
                        )}
                      >
                        <Text ff="monospace" size="sm">
                          {t.name}
                        </Text>
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{t.language}</Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge size="xs" variant="light">
                          {t.category ?? '—'}
                        </Badge>
                        {(() => {
                          const v = variantBadge(t.components);
                          return v ? (
                            <Badge size="xs" variant="outline" color="grape">
                              {v.label}
                            </Badge>
                          ) : null;
                        })()}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge color={STATUS_COLOR[t.status] ?? 'gray'} variant="light">
                          {t.status}
                        </Badge>
                        {t.status === 'REJECTED' && t.rejected_reason && (
                          <Tooltip label={t.rejected_reason} multiline w={300}>
                            <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>
                              (reason)
                            </Text>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        multiline
                        w={260}
                        label={
                          t.quality_score?.score
                            ? `Quality: ${t.quality_score.score} — Meta rates this from message-feedback signals (delivered via the quality webhook).`
                            : 'Quality stays "Pending" until the template has sent enough messages for Meta to rate it.'
                        }
                      >
                        <Badge
                          size="xs"
                          color={QUALITY_COLOR[t.quality_score?.score ?? 'PENDING'] ?? 'gray'}
                          variant="light"
                        >
                          {t.quality_score?.score ?? 'Pending'}
                        </Badge>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap={4} justify="flex-end">
                        {t.status === 'REJECTED' && (
                          <Tooltip label="Open in detail to auto-fix with AI">
                            <ActionIcon
                              renderRoot={(props) => (
                                <Link
                                  to="/vendor/templates/$name"
                                  params={{ name: t.name }}
                                  {...props}
                                />
                              )}
                              variant="subtle"
                              color="orange"
                            >
                              <IconWand size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => setConfirmDelete({ name: t.name, language: t.language })}
                          aria-label="Delete"
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        {(list.data ?? []).length === 0
                          ? 'No templates yet. Create one or sync from Meta — `hello_world` ships pre-approved.'
                          : 'No templates match the current filters.'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Delete template?"
        centered
      >
        <Stack>
          <Text size="sm">
            This deletes the template at Meta as well as locally. Active campaigns using{' '}
            <Text span ff="monospace">
              {confirmDelete?.name}
            </Text>{' '}
            will fail to send. This cannot be undone.
          </Text>
          <Group justify="end">
            <Button variant="default" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={del.isPending}
              onClick={() => confirmDelete && del.mutate({ templateName: confirmDelete.name })}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
